import { ErrorHandler, inject, Injectable } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import type { Logger } from '@posthog/core';
import { BucketedRateLimiter, resolveExceptionRateLimiterConfig } from '@posthog/core/utils';
import { AssetLoadError } from '@rma/generic/util/dynamic-asset-loader';
import { httpExceptionForCapture, isExpectedHttpFailure, isHttpErrorResponse } from './http-error-reporting';
import { PostHogService } from './posthog.service';
import { POSTHOG_EXCEPTION_CONTEXT } from './posthog-exception-context.token';

const STALE_CHUNK_MESSAGES = [
  /ChunkLoadError: Loading chunk/,
  /Importing a module script failed/,
  /Failed to fetch dynamically imported module/,
];

// Matched against the same description as the stale-chunk check, which is prefixed with the thrown
// value's name, so most are unanchored on the left.
export const NON_ACTIONABLE_ERROR_PATTERNS: readonly RegExp[] = [
  /Script error\./,
  /Error invoking postMessage: Java object is gone/,
  /Object Not Found Matching Id:/,
  /TypeError:.*bird_MV/,
  // Anchored so only an error named AbortError matches, not one whose message mentions it.
  /^AbortError(?::|\s|$)/,
];

// Fetches that the browser cancels while the page unloads. The lookahead keeps a failed lazy-route
// import ("Failed to fetch dynamically imported module") out, since that one triggers the reload.
const NAVIGATION_ABORT_PATTERNS: readonly RegExp[] = [
  /TypeError: Load failed/,
  /TypeError: Failed to fetch(?! dynamically)/,
  /LaunchDarklyFlagFetchError/,
];

const STALE_CHUNK_RELOAD_AT_KEY = 'rma.posthog.staleChunkReloadAt';
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 60_000;
const EXCEPTION_RATE_LIMIT_REFILL_INTERVAL_MS = 10_000;

// The limiter wants a logger and the handler has nothing worth logging.
const SILENT_LOGGER: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
  critical: () => undefined,
  createLogger: () => SILENT_LOGGER,
};

/** Angular hands the handler whatever was thrown, which is not always an Error. */
// eslint-disable-next-line no-restricted-syntax -- the whole point of the function is that Angular hands the handler whatever was thrown
function describe(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const candidate = value as { message?: unknown; name?: unknown } | null | undefined;

  return [candidate?.name, candidate?.message].filter((part) => typeof part === 'string').join(': ');
}

// posthog-js types a thrown value by its name and falls back to 'Error' when it has none.
// eslint-disable-next-line no-restricted-syntax -- the thrown value is untyped
function exceptionType(reported: unknown): string {
  // SAFETY: only reads `name`, and only uses it when it is a non-empty string.
  const name = (reported as { name?: unknown } | null | undefined)?.name;

  return typeof name === 'string' && name ? name : 'Error';
}

@Injectable()
export class PostHogErrorHandler implements ErrorHandler {
  private readonly posthogService = inject(PostHogService);
  private readonly window = inject<Window>(WA_WINDOW);
  private readonly context = inject(POSTHOG_EXCEPTION_CONTEXT, { optional: true });

  // client.captureException is never rate-limited, so a render loop would send every iteration.
  // Reuses the per-type limiter posthog-js applies to its own window capture, with its defaults.
  private readonly rateLimiter = new BucketedRateLimiter<string>({
    ...resolveExceptionRateLimiterConfig(),
    refillInterval: EXCEPTION_RATE_LIMIT_REFILL_INTERVAL_MS,
    _logger: SILENT_LOGGER,
  });

  // Set once the page starts leaving, because the browser aborts in-flight fetches before the
  // document is hidden. pageshow clears it (bfcache restore).
  // Deliberately no beforeunload listener: Firefox keeps a page that has one out of the bfcache,
  // which costs every back/forward a full reload to catch the few aborts that land before pagehide.
  private navigating = false;

  public constructor() {
    this.window.addEventListener('pagehide', () => {
      this.navigating = true;
    });
    this.window.addEventListener('pageshow', () => {
      this.navigating = false;
    });
  }

  public handleError(error: unknown): void {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    const originalError = (error as { originalError?: unknown } | null | undefined)?.originalError;
    // Zone.js wraps an unhandled promise rejection in an Error and keeps the rejected value on `rejection`.
    // Only an HTTP error is unwrapped, so every other rejection is handled as the wrapper it arrives in.
    // SAFETY: only reads `rejection`, and only uses it when the type guard accepts it.
    const rejection = (error as { rejection?: unknown } | null | undefined)?.rejection;
    const reported = originalError ?? (isHttpErrorResponse(rejection) ? rejection : error);

    // Ad blockers routinely stop third-party scripts, so a failed load is an event to count
    // rather than an exception to alert on.
    if (reported instanceof AssetLoadError) {
      this.posthogService.capture('AssetLoadFailed', { url: reported.url });

      return;
    }

    // Read both: Angular's wrapper carries the message, the original carries the name, and a chunk
    // failure can present as either depending on how it was thrown.
    const description = `${describe(error)} ${describe(originalError)}`;

    // Dropped before the rate limiter so that noise cannot use up the burst a real error needs.
    if (isExpectedHttpFailure(reported) || this.isNonActionable(reported, description)) {
      console.error(error);

      return;
    }

    this.report(reported, error);

    // A deploy replaces the hashed chunks, so a session that started on the old build 404s on its
    // next lazy route. Reloading picks up the new index; without it the user sees a dead screen.
    // Reported first, because navigation aborts anything still queued.
    if (STALE_CHUNK_MESSAGES.some((pattern) => pattern.test(description)) && this.claimStaleChunkReload()) {
      this.window.location.reload();
    }
  }

  // eslint-disable-next-line no-restricted-syntax -- the thrown value is untyped
  private isNonActionable(reported: unknown, description: string): boolean {
    // A script blocked by an ad blocker rejects with the DOM Event itself, which carries no stack or message.
    if (reported instanceof Event && !(reported instanceof ErrorEvent)) {
      return true;
    }

    if (NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => pattern.test(description))) {
      return true;
    }

    const leaving = this.navigating || this.window.document.visibilityState === 'hidden';

    return leaving && NAVIGATION_ABORT_PATTERNS.some((pattern) => pattern.test(description));
  }

  // If the new build is itself broken the reload fails the same way and loops forever, so only one
  // reload per cooldown is allowed. If storage cannot be read or written we reload anyway: a reload
  // loop is preferred over stranding the user on a dead screen.
  private claimStaleChunkReload(): boolean {
    const now = Date.now();

    try {
      const storage = this.window.sessionStorage;
      // No stored time reads as 0, so the elapsed time is huge and never inside the cooldown.
      const elapsed = now - Number(storage.getItem(STALE_CHUNK_RELOAD_AT_KEY));

      // A stored time in the future (clock moved back) is stale, not a reason to block for hours.
      if (elapsed >= 0 && elapsed < STALE_CHUNK_RELOAD_COOLDOWN_MS) {
        return false;
      }

      storage.setItem(STALE_CHUNK_RELOAD_AT_KEY, String(now));
    } catch {
      // Falls through to the reload: see the note above on unreadable storage.
    }

    return true;
  }

  // eslint-disable-next-line no-restricted-syntax -- both the unwrapped and the original throwable reach PostHog untyped, for the same reason
  private report(reported: unknown, original: unknown): void {
    const client = this.posthogService.client;

    if (!client) {
      console.error(original);
      return;
    }

    // Prefixed so a thrown name like `__proto__` cannot reach a prototype member of the bucket map.
    if (this.rateLimiter.consumeRateLimit(`type:${exceptionType(reported)}`)) {
      return;
    }

    const properties = this.context?.();

    const http = httpExceptionForCapture(reported, properties);

    if (http) {
      client.captureException(http.exception, http.properties);

      return;
    }

    // Absent rather than undefined, so an app with no context reports exactly as before.
    if (properties) {
      client.captureException(reported, properties);

      return;
    }

    client.captureException(reported);
  }
}
